import React from 'react';
import PropTypes from 'prop-types';
import { APP_SESSION_WARNING_TEXT, APP_SESSION_TIME_TO_CLICK } from '../config/app_config';
import { checkUserPermissions } from '../utils/common';

// Components
import ErrorBox from './shared/ErrorBox';
import LoginPage from './account/LoginPage';
import NavBar from './shared/NavBar';
import NotFound from './shared/NotFound';
import Sidebar from './shared/Sidebar';
import SuccessBox from './shared/SuccessBox';
import WarningBox from './shared/WarningBox';
import AdminSidebar from './customer_portal/admin/AdminSidebar';

export default class Template extends React.Component {
  static propTypes = {
    authError: PropTypes.string,
    accountSettings: PropTypes.object.isRequired,
    basePath: PropTypes.string.isRequired,
    children: PropTypes.node,
    dismissErrorFunction: PropTypes.func,
    dismissSuccessFunction: PropTypes.func,
    error: PropTypes.object,
    installObj: PropTypes.object,
    isAdmin: PropTypes.bool.isRequired,
    isAuthenticated: PropTypes.bool.isRequired,
    isCustomerPortal: PropTypes.bool.isRequired,
    isCustomerAdmin: PropTypes.bool.isRequired,
    isLoaded: PropTypes.bool.isRequired,
    logoutFunction: PropTypes.func.isRequired,
    params: PropTypes.object.isRequired,
    path: PropTypes.array.isRequired,
    progress: PropTypes.number.isRequired,
    requiresAuth: PropTypes.bool.isRequired,
    showWarning: PropTypes.bool.isRequired,
    success: PropTypes.object,
    user: PropTypes.object,
  };

  static defaultProps = {
    authError: '',
    children: null,
    dismissErrorFunction: null,
    dismissSuccessFunction: null,
    error: null,
    installObj: null,
    success: null,
    user: null,
  };

  // We don't need to do anything to keep alive. The click does it.
  keepAlive = () => false;

  cancelSession = () => {
    const { logoutFunction } = this.props;
    logoutFunction();
  };

  renderChildren = () => {
    const {
      accountSettings,
      installObj,
      isAdmin,
      isCustomerPortal,
      isCustomerAdmin,
      basePath,
      children,
      dismissErrorFunction,
      dismissSuccessFunction,
      error,
      params,
      path,
      showWarning,
      success,
      user,
    } = this.props;
    const userTypeId = user.get('userTypeId');
    const isUserAuthorized = checkUserPermissions(userTypeId, path);
    const installId = params.instanceId;
    const cpPage = params.page === 'home' ? 'admin-list' : params.page;
    const isCPAdminPage = path[0] === 'admin' || path[0] === 'search';
    const hideAdminSidebar = path[0] === 'search';
    const cpWrapperStyle = isCPAdminPage
      ? `customer-portal content-wrapper ${hideAdminSidebar ? 'no-admin-sidebar' : ''}`
      : 'container no-sidebar';

    // TODO find constant for superadmin name
    let highlightPath = basePath;
    if (highlightPath === 'superadmin' && path[1]) [, highlightPath] = path;

    return (
      <div className={`${isCustomerPortal ? cpWrapperStyle : 'content-wrapper'}`}>
        {isCustomerPortal ? null : (
          <Sidebar
            highlight={highlightPath}
            isAdmin={isAdmin}
            userTypeId={user.userTypeId}
            accountSettings={accountSettings}
          />
        )}
        {isCustomerPortal && isCustomerAdmin && cpPage ? (
          <AdminSidebar
            data={installObj}
            highlight={cpPage}
            installId={installId}
            isAdmin={isCustomerAdmin}
            userTypeId={userTypeId}
          />
        ) : null}
        <div className="page-content-wrapper">
          <div className="container-fluid">
            <div className="row">
              <div className="col-xs-12">
                <div className={isCustomerAdmin ? 'm-l-1' : ''}>
                  {error ? (
                    <ErrorBox error={error} dismissErrorFunction={dismissErrorFunction} />
                  ) : null}
                  {success ? (
                    <SuccessBox success={success} dismissSuccessFunction={dismissSuccessFunction} />
                  ) : null}
                  {showWarning === true ? (
                    <WarningBox
                      time={APP_SESSION_TIME_TO_CLICK}
                      text={APP_SESSION_WARNING_TEXT}
                      cancelFunction={this.cancelSession}
                      cancelText="Log Out"
                      keepAliveFunction={this.keepAlive}
                      keepAliveText="Stay Logged In"
                    />
                  ) : null}
                </div>

                {isUserAuthorized ? children : <NotFound />}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  renderNoSidebar = pageType => {
    const {
      authError,
      children,
      dismissErrorFunction,
      dismissSuccessFunction,
      error,
      isCustomerPortal,
      success,
    } = this.props;
    return (
      <div
        className={
          isCustomerPortal ? 'customer-portal container no-sidebar' : 'container no-sidebar'
        }
      >
        <div className="row">
          <div className="col-xs-12">
            {error ? <ErrorBox error={error} dismissErrorFunction={dismissErrorFunction} /> : null}
            {success ? (
              <SuccessBox success={success} dismissSuccessFunction={dismissSuccessFunction} />
            ) : null}
            {pageType === 'login' ? <LoginPage authError={authError} /> : null}
          </div>
        </div>
        {pageType === 'children' ? children : null}
      </div>
    );
  };

  render() {
    const {
      accountSettings,
      isAdmin,
      isAuthenticated,
      isCustomerPortal,
      isCustomerAdmin,
      isLoaded,
      logoutFunction,
      progress,
      requiresAuth,
      user,
    } = this.props;

    return (
      <div className={isLoaded ? 'top-wrapper isLoaded' : 'top-wrapper'}>
        <NavBar
          accountSettings={accountSettings}
          isAdmin={isAdmin}
          isCustomerPortal={isCustomerPortal}
          isCustomerAdmin={isCustomerAdmin}
          isAuthenticated={isAuthenticated}
          logoutFunction={logoutFunction}
          user={user}
          progress={progress}
        />

        {/* Page doesn't require authentication */}
        {!requiresAuth ? this.renderNoSidebar('children') : null}

        {/* Page requires authentication and has it */}
        {requiresAuth && isAuthenticated ? this.renderChildren() : null}

        {/* Page requires authentication and doesn't have it */}
        {requiresAuth && !isAuthenticated && progress < 1 ? this.renderNoSidebar('login') : null}
      </div>
    );
  }
}
